import React, { useEffect, useState } from 'react';

import { LoginModal, Props as LoginModalProps } from './LoginModal';
import {
  ModalPage,
  PartialClickTrackingOptions,
  PartialConfigurableText,
  RegistrationParams,
} from './types';
import {
  lookupModalVariant,
  mergeConfiguredText,
  substituteWorkContext,
} from './modalVariantRegistry';
import { abTest as bucketLoswpModalAutotest } from 'js/ab_tests/tests/loswp_modal_autotest_2026_q2_v5';
import Cookies from 'js-cookie';
import routes from 'js/core_pre_webpack/routes';

type LoginModalEventDetail = {
  postLoginRedirectUrl?: string;
  forcePostLoginRedirectUrl?: boolean;
  clickTrackingContext?: PartialClickTrackingOptions;
  configuredText?: PartialConfigurableText;
  registrationParams: RegistrationParams;
  initialPage?: ModalPage;
  // Work context: substituted into `{paperTitle}`/`{authorName}` tokens and
  // rendered by the yourPaperCard header variant.
  workTitle?: string;
  authorName?: string;
  workThumbnailUrl?: string;
  workYear?: number;
  workViewCount?: number;
};

// # Usage
//
// It isn't necessary to use openLoginModal directly. The LoginModalManager will listen for
// the 'open-login-modal' event and open the modal when it is triggered. Example:
//
// document.dispatchEvent(new CustomEvent('open-login-modal', {
//   detail: {
//     postLoginRedirectUrl: '/dashboard',
//     configuredText: {
//       buttonsPage: {
//         header: 'Custom welcome message'
//       }
//     },
//     registrationParams: {
//       user_source: 'header_button'
//     }
//   }
// }));
//
export const openLoginModal = (detail: LoginModalEventDetail) => {
  document.dispatchEvent(new CustomEvent('open-login-modal', { detail }));
};

export const LoginModalManager: React.FC = () => {
  const [modalProps, setModalProps] = useState<LoginModalProps>({
    open: false,
    registrationParams: {
      user_source: 'unknown', // user_source is required in event so this should never actually be sent
    },
  });

  useEffect(() => {
    const handleOpenLoginModal = (
      event: CustomEventInit<LoginModalEventDetail>,
    ) => {
      const { detail } = event;
      if (!detail) {
        throw new Error('No detail provided to open-login-modal event');
      }

      if (
        !document.querySelector<HTMLMetaElement>("[name='csrf-token']") &&
        !Cookies.get('masked_csrf_token')
      ) {
        window.location.href = routes.login_path({
          post_login_redirect_url: encodeURIComponent(window.location.href),
          modal_fallback: 'true',
        });
      } else {
        // Bucket at open (CTA click), not mount, so only engaged visitors are
        // recorded. Variant is pure config — no chunk — so open is synchronous.
        const variant = bucketLoswpModalAutotest();
        const variantEntry = lookupModalVariant(variant);
        setModalProps(() => ({
          open: true,
          postLoginRedirectUrl: detail.postLoginRedirectUrl,
          forcePostLoginRedirectUrl: detail.forcePostLoginRedirectUrl,
          clickTrackingContext: detail.clickTrackingContext,
          configuredText: substituteWorkContext(
            mergeConfiguredText(
              detail.configuredText,
              variantEntry.configuredText,
            ),
            { workTitle: detail.workTitle, authorName: detail.authorName },
          ),
          registrationParams: detail.registrationParams,
          initialPage: detail.initialPage,
          headerVariant: variantEntry.headerVariant,
          ctaLayout: variantEntry.ctaLayout,
          workTitle: detail.workTitle,
          authorName: detail.authorName,
          workThumbnailUrl: detail.workThumbnailUrl,
          workYear: detail.workYear,
          workViewCount: detail.workViewCount,
        }));
      }
    };

    document.addEventListener('open-login-modal', handleOpenLoginModal);

    return () => {
      document.removeEventListener('open-login-modal', handleOpenLoginModal);
    };
  }, []);

  const handleClose = () => {
    setModalProps((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return <LoginModal {...modalProps} onClose={handleClose} />;
};
